import { z } from 'zod';
import { AccountSchema } from '../account';

/**
 * Snapshot of the `Account` at the moment a transaction was posted,
 * embedded directly inside the `AccountTransaction` doc.
 *
 * Mirrors {@link transactionWalletSchema} (the snapshot embedded
 * inside a `Transaction` doc) BUT preserves the full
 * {@link partySchema} on each party rather than flattening to
 * `holderId` / `holderName` / `holderType`. That choice is
 * deliberate:
 *
 * - The Account itself stores `parties: [partySchema, partySchema]`
 *   for richer downstream resolution (`roles[]`, `ids[]`, etc.).
 * - Flattening here would force every consumer (drawer, list,
 *   export) to special-case the snapshot vs the Account.
 * - The snapshot is still cheap (two parties, not a tree).
 *
 * `balance` here is **POST-tx** — the balance AFTER this transaction
 * has been applied to the Account. Same convention as
 * {@link transactionWalletSchema.balance}.
 */
const accountPick = AccountSchema.data.pick({ currency: true, parties: true });

export const accountTransactionAccountSchema = accountPick.extend({
  /** the Account `_id` */
  _id: AccountSchema.model.shape._id,
  /** the Account balance `AFTER` this transaction (signed, from `parties[0]`'s POV) */
  balance: AccountSchema.data.shape.balance,
  /** the Account currency */
  currency: AccountSchema.data.shape.currency,
  /**
   * The two parties as full party snapshots, in the same canonical
   * order as the source `Account.parties[]`.
   */
  parties: AccountSchema.data.shape.parties,
});
export type AccountTransactionAccount = z.infer<typeof accountTransactionAccountSchema>;
